import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
} from '@mui/material';
import {
  Warning,
  Person,
  Email,
  Phone,
} from '@mui/icons-material';
import { TransporterWithPackages } from '@otw/models';
import { formatTransporterName } from '../common';
import { LogisticsUserGridRow } from '../views/UsersGrid';

interface DeleteTransporterDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  transporter: LogisticsUserGridRow | null;
  isDeleting?: boolean;
}

const DeleteTransporterDialog: React.FC<DeleteTransporterDialogProps> = ({
  open,
  onClose,
  onConfirm,
  transporter,
  isDeleting = false,
}) => {
  if (!transporter) return null;

  const displayName = formatTransporterName(transporter);
  const packageCount = transporter.packages?.length || 0;
  const dual = transporter.hasTransportAccess && transporter.hasRepairAccess

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning color="error" />
          <Typography variant="h6" component="div">
            Delete logistics user
          </Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 3 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="medium">
            This action cannot be undone. Their Firebase account and users document will be removed, along with
            transport and/or repair profiles as applicable.
          </Typography>
        </Alert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          {dual
            ? 'This user has both transport and repair access. The entire account will be deleted.'
            : 'Are you sure you want to delete this user?'}
        </Typography>

        <Box sx={{ 
          p: 2, 
          backgroundColor: 'grey.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Person fontSize="small" sx={{ color: 'primary.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {displayName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Email fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="body2">
              {transporter.email}
            </Typography>
          </Box>
          
          {transporter.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Phone fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2">
                {transporter.phone}
              </Typography>
            </Box>
          )}

          {transporter.hasTransportAccess && (
            <Typography variant="body2" color="text.secondary">
              Packages: {packageCount} package{packageCount !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {transporter.hasTransportAccess && packageCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This transporter has {packageCount} package{packageCount !== 1 ? 's' : ''} associated with them.
              These packages will be unassigned and set to DRAFT status.
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ p: 2 }}>
        <Button 
          onClick={onClose} 
          disabled={isDeleting}
          variant="outlined" 
          color="secondary"
        >
          Cancel
        </Button>
        <Button 
          onClick={onConfirm} 
          disabled={isDeleting}
          variant="contained" 
          color="error"
        >
          {isDeleting ? 'Deleting...' : 'Delete user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteTransporterDialog;
